<template>
  <div class="screentime-management"
       :class="screenTimeStyles">
    <div class="body">
      <base-switch class="periods-switch"
                   v-model="selectedPeriod"
                   :options="getPeriods()"
                   @change="resetTimeLimit"/>

      <div class="used-time section">
        <div class="section-title">
          {{
            selectedPeriod.key === 'TODAY'
              ? $t("screentimeManagement.usedToday")
              : $t("screentimeManagement.dayLimits")
          }}
        </div>

        <div class="section-main-content">
          <template v-if="selectedPeriod.key === 'TODAY'">
            {{ usedScreenTime | prettyTime }}
          </template>

          <day-picker v-else
                      :time-limits="weekTimeLimits"
                      @toggle="toggleDay"/>
        </div>
      </div>

      <div class="left-time section">
        <div class="section-title">
          {{
            selectedPeriod.key === 'TODAY'
              ? $t("screentimeManagement.overrideForToday")
              : $t("screentimeManagement.setDailyAvailable")
          }}
        </div>

        <div class="section-main-content">
          <template v-if="isTimeUnlimited">
            {{ $t("screentimeManagement.unlimited") }}
          </template>

          <base-time-picker v-else
                            v-model="leftScreenTime"/>
        </div>

        <base-checkbox class="limit-checkbox"
                       v-model="isTimeUnlimited">
          {{ $t("screentimeManagement.unlimitedScreentime") }}
        </base-checkbox>

        <div class="section-additional-info">
          {{ $t("screentimeManagement.screentimeAggregated") }}
        </div>

        <mode-picker v-if="show"
                     :child="child"/>
      </div>
    </div>

    <div class="footer">
      <base-button class="submit-button"
                   color="secondary"
                   :loading="isSettingNewLimit"
                   @click="setNewTimeLimit">
        {{
          selectedPeriod.key === 'TODAY'
            ? $t("screentimeManagement.setTodayTime")
            : $t("screentimeManagement.setScreentime")
        }}

        <template v-if="isTimeUnlimited">
          {{ $t("screentimeManagement.dontLimit") }}
        </template>

        <template v-else>
          {{ leftScreenTime | prettyTime }}
        </template>
      </base-button>
    </div>
  </div>
</template>

<script>
  import DayPicker from './ScreenTimeManagementDayPicker.vue'
  import ModePicker from './ScreenTimeManagementModePicker.vue'
  import { getTimeString } from '@/services/utils.js'
  import * as intl from '@/services/intl'

  import analytics from '@/services/analytics.js'
  import i18n from '@/services/i18n.js'

  const unlimitedTime = 1440
  const defaultLimit = 8 * 60
  
  export default {
    components: {
      DayPicker,
      ModePicker
    },

    props: {
      childId: {
        type: String,
        required: true
      },

      show: {
        type: Boolean,
        required: false
      }
    },

    created() {
      analytics.sendEvent('ScrTimeManage_Sc');
      this.selectedPeriod = this.periods[0];
    },

    data() {
      return {
        periods: [
          { key: 'TODAY', value: i18n.t("today") },
          { key: 'DAILY', value: i18n.t("daily") }
        ],
        selectedPeriod: null,
        currentDate: null,
        isTimeUnlimited: false,
        сurrentDayLimit: 0,
        leftScreenTime: 0,
        weekTimeLimits: [],
        isSettingNewLimit: false
      }
    },

    computed: {
      child() {
        return this.getChildById(this.childId)
      },

      usedScreenTime() {
        let isScreenTimeUsed = this.child.latest_accumulated_screentime && this.child.latest_accumulated_screentime.date === this.currentDate

        return isScreenTimeUsed ? Math.floor(this.child.latest_accumulated_screentime.sum_in_seconds / 60) : 0
      },

      toggleLabel(key) {
        return i18n.t(key.toLowerCase());
      },

      currentWeekTimeLimits() {
        return this.child.settings.screen_time_limit_in_minutes
      },

      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      screenTimeStyles() {
        return {
          'screentime-management-mobile': this.$mq === 'mobile'
        }
      },

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      })
    },

    methods: {
      getPeriods() {
        return this.periods;
      },

      setWeekTimeLimits() {
        const currentDayIndex = intl.getDayOfTheWeek()
        this.weekTimeLimits = this.currentWeekTimeLimits.map((limit, dayIndex) => {
          return {
            selected: currentDayIndex === dayIndex,
            limit: limit === unlimitedTime ? this.$t("unltd") : this.$options.filters.prettyTime(limit),
            name: intl.getDayNameByIndex(dayIndex)
          }
        })
      },

      resetTimeLimit() {
        if (this.selectedPeriod.key === 'TODAY') {
          this.setTodayLimit()
        } else {
          this.setCurrentDayOfWeekLimit()
          analytics.sendEvent('WklyScrTime_Sc')
        }
      },

      setTodayLimit() {
        let currentDayOfWeekLimit = this.currentWeekTimeLimits[intl.getDayOfTheWeek()]
        let isTodayLimitSet = this.child.settings.screen_time_override_date === this.currentDate
        let todayLimit = isTodayLimitSet ? this.child.settings.screen_time_override_in_minutes : currentDayOfWeekLimit

        this.isTimeUnlimited = todayLimit === unlimitedTime

        if (this.isTimeUnlimited) {
          this.сurrentDayLimit = currentDayOfWeekLimit === unlimitedTime ? defaultLimit : currentDayOfWeekLimit
        } else {
          this.сurrentDayLimit = todayLimit
        }

        this.leftScreenTime = this.usedScreenTime < this.сurrentDayLimit ? this.сurrentDayLimit - this.usedScreenTime : 0
      },

      setCurrentDayOfWeekLimit() {
        this.сurrentDayLimit = this.currentWeekTimeLimits[intl.getDayOfTheWeek()]
        this.isTimeUnlimited = this.сurrentDayLimit === unlimitedTime
        this.leftScreenTime = this.isTimeUnlimited ? defaultLimit : this.сurrentDayLimit

        this.setWeekTimeLimits()
      },

      toggleDay(day) {
        day.selected = !day.selected
      },

      setNewTimeLimit() {
        analytics.sendEvent('St_SetTime_ScrTimeManage')
        this.isSettingNewLimit = true

        let setNewTimeLimit = this.selectedPeriod.key === 'TODAY' ? this.setNewTodayTimeLimit : this.setNewWeekTimeLimits

        setNewTimeLimit().then(() => {
          this.showSuccessNotification()
          this.$store.dispatch('children/loadStatus', this.child.id)
        }).catch(() => {
          this.showFailNotification()
        }).then(() => {
          this.isSettingNewLimit = false

          //this.resetTimeLimit()
        })
      },

      showSuccessNotification() {
        this.$notify({
          type: 'success',
          text: 'Screen Time Limit is set'
        })
      },

      showFailNotification() {
        this.$notify({
          type: 'error',
          text: 'Failed to set Screen Time Limit'
        })
      },

      setNewTodayTimeLimit() {
        let leftScreenTime = this.isTimeUnlimited ? unlimitedTime : this.leftScreenTime + this.usedScreenTime

        return this.updateChildSettings({
          child_id: this.child.id,
          data: {
            screen_time_override_date: this.currentDate,
            screen_time_override_in_minutes: leftScreenTime > unlimitedTime ? unlimitedTime : leftScreenTime
          }
        })
      },

      setNewWeekTimeLimits() {
        let newLimit = this.isTimeUnlimited ? unlimitedTime : this.leftScreenTime
        let newWeekTimeLimits = this.weekTimeLimits.map((day, index) => {
          return day.selected ? newLimit : this.currentWeekTimeLimits[index]
        })

        return this.updateChildSettings({
          child_id: this.child.id,
          data: {
            screen_time_limit_in_minutes: newWeekTimeLimits
          }
        })
      },

      ...mapActions('children', {
        updateChildSettings: 'updateChildSettings'
      })
    },

    watch: {
      child: {
        handler(newValue, oldValue) {
          if (!newValue || !oldValue || newValue.id !== oldValue.id) {
            return
          }

          if (JSON.stringify(newValue.settings.screen_time_limit_in_minutes) !== JSON.stringify(oldValue.settings.screen_time_limit_in_minutes)) {
            this.setWeekTimeLimits()
          }
        },
        immediate: true,
        deep: true
      },

      show: {
        handler(show) {
          if (!show) {
            return
          }

          this.selectedPeriod = this.periods[0]
          this.currentDate = intl.getISODate()
          this.setTodayLimit()
        },
        immediate: true
      },

      isTimeUnlimited() {
        if (this.selectedPeriod.key === 'TODAY') {
          analytics.sendEvent('Tg_SetUnlmtd_TodayScrTime')
        }

        if (this.selectedPeriod.key === 'DAILY') {
          analytics.sendEvent('Tg_SetUnlmtd_WklyScrTime')
        }
      }
    },

    filters: {
      prettyTime(timeInMinutes) {
        let hours = Math.floor(timeInMinutes / 60)
        let minutes = timeInMinutes % 60

        var prettyStr = hours + i18n.t("hoursAbbrev");
        if(minutes)
        {
          prettyStr = prettyStr + ' ' + getTimeString(minutes) + i18n.t("minutesAbbrev");
        }

        return prettyStr;
      }
    },

    destroyed() {
      this.showModal('deviceAvailability', {
        childId: this.child.id,
        screentimeExhausted: !_.isNil(this.child.settings.screen_time_override_in_minutes) &&
          this.child.settings.screen_time_override_in_minutes <= 0,
        showScheduleBtn: false
      })
    }
  }
</script>

<style lang="scss" scoped>
  .screentime-management {
    width: 100%;
    height: auto;
  }

  .periods-switch {
    margin-bottom: 36px;
  }

  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .section-title {
    font-size: 20px;
    font-weight: bold;
    letter-spacing: 1.4px;
    color: rgba(#000, .5);
    text-transform: uppercase;
  }

  .section-main-content {
    font-weight: bold;
    color: map.get($colors, primary);
  }

  .section-additional-info {
    width: 223px;
    margin-bottom: 24px;
    font-size: 14px;
    line-height: 1.4;
    color: #3e3a3b;
    text-align: center;
  }

  .section.used-time {
    margin-bottom: 40px;

    .section-title {
      margin-bottom: 12px;
    }

    .section-main-content {
      font-size: 36px;
    }
  }

  .section.left-time {
    .section-title {
      margin-bottom: 24px;
    }

    .section-main-content {
      margin-bottom: 24px;
      font-size: 47px;
    }
  }

  .limit-checkbox {
    margin-bottom: 17px;
  }

  .footer {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 21px 30px;

    .submit-button {
      width: 100%;
      height: 75px;
      padding: 23px;
      font-size: 24px;
      font-weight: bold;
    }
  }

  .screentime-management-mobile {
    .body {
      .section {
        .section-title {
          font-size: 16px;
        }
      }
    }

    .footer {
      padding: 21px 30px;

      .submit-button {
        padding: 15px;
      }
    }
  }
</style>
